/**
 * Browser-safe row↔doc utilities + the LiveSet merge engine — the pure core of
 * the Supabase Realtime subscription layer (migration plan Phase 4) and of the
 * Node adapter's read path. NO pg / supabase-js imports: the portal bundles
 * this via the `@djgg/db/rows` export.
 *
 * Realtime (postgres_changes) delivers ROW-level change events; the portal's
 * Subscriptions contract emits Firestore-onSnapshot-style FULL result sets.
 * {@link LiveSet} bridges the two: seed it with the initial SELECT, feed it
 * change events, and it re-emits the merged, ordered, limited result set.
 */
import { snakeToCamel, type TableDef } from './registry.js';

export type Row = Record<string, unknown>;

/**
 * The client-readable `creations` projection. Keep this in lockstep with
 * 0054_analysis_draft_read_privacy.sql: browser roles deliberately cannot
 * select the server-owned `analysis_draft` column.
 */
export const CREATIONS_CLIENT_COLUMNS = [
  'id',
  'slug',
  'kind',
  'title',
  'description',
  'style',
  'creator_uid',
  'creator_handle',
  'template',
  'engine_provider',
  'engine_model',
  'status',
  'build_state',
  'latest_version',
  'latest_bundle_url',
  'active_version',
  'active_bundle_url',
  'likes_count',
  'comments_count',
  'views_count',
  'distinct_viewers',
  'plays_count',
  'visibility',
  'settings',
  'settings_schema_version',
  'settings_rev',
  'cover_url',
  'title_overlay_url',
  'title_overlay_for_title',
  'assets',
  'created_at',
  'updated_at',
  'fork_parent_id',
  'root_creator_uid',
  'root_creator_handle',
  'forked_from',
  'diverged_at',
  'forks_count',
  'concept',
  'game_design',
  'design_rev',
  'reference_briefs',
  'editors_pick',
  'suggested_iterate_prompt',
  'suggested_asset_prompt',
  'preview_url',
  'poster_url',
  'video_briefs',
  'last_played_at',
  'rating',
  'published_at',
] as const;

export const CREATIONS_CLIENT_SELECT = CREATIONS_CLIENT_COLUMNS.join(', ');

/**
 * Convert one Postgres row to its domain doc: snake→camel, unflatten the
 * descriptor's nested objects (engine, tokenUsage, …), spread the extras
 * overflow column. PURE — the creations editors/memberUids reconstruction
 * (which needs creation_members rows) is layered on via
 * {@link stitchCreationMembers}.
 */
export function rowToDoc(def: TableDef, row: Row): Row {
  const flatCols = new Map<string, [string, string]>();
  for (const [docField, nested] of Object.entries(def.flatten ?? {})) {
    for (const [k, col] of Object.entries(nested)) flatCols.set(col, [docField, k]);
  }
  const doc: Row = {};
  for (const [col, value] of Object.entries(row)) {
    const flat = flatCols.get(col);
    if (flat) {
      const [docField, k] = flat;
      doc[docField] = { ...((doc[docField] as Row) ?? {}), [k]: value };
      continue;
    }
    if (def.extrasColumn && col === def.extrasColumn) {
      Object.assign(doc, (value as Row) ?? {});
      continue;
    }
    doc[snakeToCamel(col)] = value;
  }
  return doc;
}

/** The PostgREST embed selecting exactly the {@link MemberRow} columns — ONE
 *  definition for every consumer (portal subscriptions + browser adapter);
 *  four drifting string copies was a review finding. */
export const MEMBERS_EMBED = 'creation_members(uid, role, handle, added_at)';

export const CREATIONS_WITH_MEMBERS_SELECT = `${CREATIONS_CLIENT_SELECT}, ${MEMBERS_EMBED}`;

/** One member row from `creation_members` (already camelCased or raw). */
export interface MemberRow {
  creation_id?: string;
  uid: string;
  role: string;
  handle: string;
  added_at?: number;
}

/**
 * Reconstruct the domain's `editors` + `memberUids` fields on a creations doc
 * from its creation_members rows (sorted by added_at; creator never listed as
 * an editor; memberUids = [creatorUid, ...editor uids]).
 */
export function stitchCreationMembers(doc: Row, members: MemberRow[]): Row {
  const sorted = [...members].sort((a, b) => (a.added_at ?? 0) - (b.added_at ?? 0));
  const editors = sorted
    .filter((m) => m.role === 'editor')
    .map((m) => ({ uid: m.uid, handle: m.handle }));
  return {
    ...doc,
    editors,
    memberUids: [doc.creatorUid as string, ...editors.map((e) => e.uid)],
  };
}

/** The subset of a postgres_changes payload LiveSet consumes. */
export interface ChangeEvent {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: Row | null;
  old?: Row | null;
}

/**
 * A keyed, ordered, bounded live result set. Seed with the initial SELECT
 * rows, {@link apply} each Realtime change, read {@link rows} for the merged
 * set — the onSnapshot-equivalence layer.
 *
 * DELETE events under REPLICA IDENTITY FULL carry the full old row; the key is
 * extracted from `old`. Events for rows outside the set's filter should be
 * screened by the caller BEFORE apply (server-side Realtime filters + RLS do
 * most of that; client-side residual filters cover the rest).
 */
export class LiveSet<T extends Row = Row> {
  private readonly byKey = new Map<string, T>();

  constructor(
    private readonly keyOf: (row: Row) => string,
    private readonly compare?: (a: T, b: T) => number,
    private readonly limit?: number,
  ) {}

  seed(rows: T[]): void {
    this.byKey.clear();
    for (const r of rows) this.byKey.set(this.keyOf(r), r);
  }

  /** Apply one change; returns true when the set changed. */
  apply(ev: ChangeEvent): boolean {
    if (ev.eventType === 'DELETE') {
      const key = ev.old ? this.keyOf(ev.old) : undefined;
      if (key === undefined || !this.byKey.has(key)) return false;
      this.byKey.delete(key);
      return true;
    }
    if (!ev.new) return false;
    this.byKey.set(this.keyOf(ev.new), ev.new as T);
    return true;
  }

  /** Merged result set: ordered (when a comparator is set) and limit-bounded. */
  rows(): T[] {
    let out = [...this.byKey.values()];
    if (this.compare) out = out.sort(this.compare);
    if (this.limit !== undefined && out.length > this.limit) out = out.slice(0, this.limit);
    return out;
  }

  get size(): number {
    return this.byKey.size;
  }
}

/** Comparator helper: order by a numeric field. */
export function byNumber<T extends Row>(field: string, dir: 'asc' | 'desc' = 'asc') {
  return (a: T, b: T): number => {
    const av = Number(a[field] ?? 0);
    const bv = Number(b[field] ?? 0);
    return dir === 'asc' ? av - bv : bv - av;
  };
}
